/**
 * Which currency to print beside a Credit Pack's price.
 *
 * Every pack is priced at the same number in USD, GBP, EUR and CHF (parity), and Stripe
 * Checkout charges the one matching the buyer's location, falling back to USD (Adaptive Pricing
 * stays off). The page cannot see the IP Stripe goes by, so it guesses the same location from the
 * browser's time zone. A wrong guess changes the symbol and never the number, and the note under
 * the packs says the charge follows the buyer's location.
 */

export type PackCurrency = "USD" | "GBP" | "EUR" | "CHF";

// Zone ids as browsers report them: CLDR keeps the country-specific ids (Europe/Oslo is not
// folded into Europe/Berlin), so each id below names a place whose local currency is one of the
// four. Anywhere else, Stripe falls back to USD, and so does this.
const GBP_ZONES = new Set([
  "Europe/London",
  "Europe/Belfast",
  "Europe/Guernsey",
  "Europe/Isle_of_Man",
  "Europe/Jersey",
]);

const CHF_ZONES = new Set(["Europe/Zurich", "Europe/Vaduz"]);

// The euro area as of 2026 (Bulgaria joined on 1 January 2026), the outermost regions that use
// the euro, and the non-EU states that use it with a zone of their own.
const EUR_ZONES = new Set([
  "Europe/Vienna",
  "Europe/Brussels",
  "Europe/Sofia",
  "Europe/Zagreb",
  "Asia/Nicosia",
  "Asia/Famagusta",
  "Europe/Nicosia",
  "Europe/Tallinn",
  "Europe/Helsinki",
  "Europe/Mariehamn",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Busingen",
  "Europe/Athens",
  "Europe/Dublin",
  "Europe/Rome",
  "Europe/Riga",
  "Europe/Vilnius",
  "Europe/Luxembourg",
  "Europe/Malta",
  "Europe/Amsterdam",
  "Europe/Lisbon",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Europe/Bratislava",
  "Europe/Ljubljana",
  "Europe/Madrid",
  "Africa/Ceuta",
  "Atlantic/Canary",
  "America/Guadeloupe",
  "America/Martinique",
  "America/Cayenne",
  "Indian/Reunion",
  "Indian/Mayotte",
  "Europe/Monaco",
  "Europe/San_Marino",
  "Europe/Vatican",
  "Europe/Andorra",
  "Europe/Podgorica",
]);

/** The currency Stripe would charge someone in `timeZone`. */
export const packCurrencyFor = (timeZone: string | undefined): PackCurrency => {
  if (!timeZone) return "USD";
  if (GBP_ZONES.has(timeZone)) return "GBP";
  if (CHF_ZONES.has(timeZone)) return "CHF";
  if (EUR_ZONES.has(timeZone)) return "EUR";
  return "USD";
};

/** The currency for whoever is looking at the page. */
export const viewerPackCurrency = (): PackCurrency => {
  try {
    return packCurrencyFor(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return "USD";
  }
};

/**
 * `4150` as `$4,150`, `£4,150`, `€4,150` or `CHF 4,150`. Always `en-US`, so the separators match
 * the rest of the page whatever the currency. Pack amounts are whole units, so no decimals.
 */
export const formatPackAmount = (amount: number, currency: PackCurrency): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

/**
 * A price per credit, `83` as `$83.00` and `0.85` as `$0.85`. A pack's amount divided by its
 * credits rarely comes out whole, so this one keeps two decimals where the amounts keep none.
 * For display only: comparing two rates is done on the whole numbers they come from.
 */
export const formatCreditRate = (rate: number, currency: PackCurrency): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate);
